/**
 * Profile Service Client
 *
 * Provides a centralized client for all profile-related operations.
 * Replaces direct Supabase calls with API calls where available.
 *
 * Note: Some profile operations may still use Supabase directly if no API endpoint exists.
 * This service provides a consistent interface for future migration.
 */

import { API_ENDPOINTS, getAuthHeader } from "@/config/api";
import { isExpectedClientAuthError, isTransientClientFetchError } from "@/utils/apiClientErrors";
import { getAuthToken } from "./assets/assetHelpers";
import { authService } from "./auth/index";
import { dedupeInFlightGet } from "@/utils/inFlightGetDedup";
import { fileToBase64 } from "@/utils/fileToBase64";

export interface Profile {
  id: string;
  email?: string;
  full_name?: string;
  display_name?: string;
  avatar_url?: string;
  phone?: string;
  location?: string;
  occupation?: string;
  bio?: string;
  website?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  timezone?: string;
  language?: string;
  currency?: string;
  role?: string;
  created_at: string;
  updated_at: string;
}

export interface ProfileRequestOptions {
  signal?: AbortSignal;
}

/**
 * Create Request Abort Signal.
 */
function createRequestAbortSignal(
  timeoutMs: number,
  clientSignal?: AbortSignal
): { signal: AbortSignal; cleanup: () => void } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const onClientAbort = () => controller.abort();

  if (clientSignal) {
    if (clientSignal.aborted) {
      controller.abort();
    } else {
      clientSignal.addEventListener("abort", onClientAbort);
    }
  }

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      if (clientSignal) {
        clientSignal.removeEventListener("abort", onClientAbort);
      }
    },
  };
}

/**
 * Build Request Headers.
 */
function buildRequestHeaders(
  method: string,
  token: string | null,
  optionHeaders: globalThis.HeadersInit | undefined
): Promise<globalThis.HeadersInit> {
  const isStateChangingOperation = ["POST", "PUT", "DELETE", "PATCH"].includes(method);
  if (isStateChangingOperation) {
    return authService.getAuthHeaders();
  }
  return {
    ...getAuthHeader(token || undefined),
    ...optionHeaders,
  };
}

function computeRateLimitDelay(response: Response, attempt: number): number {
  const retryAfter = response.headers.get("Retry-After");
  if (retryAfter) {
    const retrySeconds = Number.parseInt(retryAfter, 10);
    return Math.min(retrySeconds * 1000, 60000);
  }
  return Math.min(1000 * 2 ** attempt, 10000);
}

/**
 * Parse Http Error.
 */
async function parseHttpError(response: Response): Promise<Error & { status: number }> {
  let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
  try {
    const errorData = await response.json();
    errorMessage = errorData.error || errorMessage;
  } catch {
    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
  }
  const error = new Error(errorMessage) as Error & { status: number };
  error.status = response.status;
  return error;
}

/**
 * Rethrow Abort As Timeout.
 */
function rethrowAbortAsTimeout(
  error: unknown,
  clientSignal: AbortSignal | undefined,
  timeoutMs: number
): never {
  if (error instanceof Error && error.name === "AbortError") {
    if (clientSignal?.aborted) {
      throw error;
    }
    const timeoutError = new Error(`Request timeout after ${timeoutMs}ms`) as Error & {
      status: number;
    };
    timeoutError.status = 408;
    throw timeoutError;
  }
  throw error;
}

/**
 * Make authenticated API call with timeout and retry logic for rate limiting
 */
function apiCall<T>(
  endpoint: string,
  options: globalThis.RequestInit = {},
  clientSignal?: AbortSignal
): Promise<T> {
  const method = options.method || "GET";

  /**
   * Execute Request.
   */
  const executeRequest = async (attempt: number): Promise<T> => {
    const token = await getAuthToken();
    const headers = await buildRequestHeaders(method, token, options.headers);

    const timeoutMs = 30000;
    const { signal, cleanup } = createRequestAbortSignal(timeoutMs, clientSignal);

    try {
      const response = await fetch(endpoint, {
        ...options,
        headers,
        credentials: "include",
        signal,
      });

      cleanup();

      if (response.status === 429) {
        const maxRetries = 2;
        if (attempt < maxRetries) {
          const delay = computeRateLimitDelay(response, attempt);
          console.warn(
            `[ProfileService] Rate limited (429), retrying after ${Math.round(delay / 1000)}s (attempt ${attempt + 1}/${maxRetries})`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          return executeRequest(attempt + 1);
        }
        const error = new Error(`HTTP 429: Too Many Requests (max retries exceeded)`) as Error & {
          status: number;
        };
        error.status = 429;
        throw error;
      }

      if (!response.ok) {
        throw await parseHttpError(response);
      }

      return response.json();
    } catch (error: unknown) {
      cleanup();
      rethrowAbortAsTimeout(error, clientSignal, timeoutMs);
    }
  };

  if (method === "GET") {
    return dedupeInFlightGet(endpoint, () => executeRequest(0));
  }

  return executeRequest(0);
}

/**
 * Profile Service
 *
 * Provides centralized profile operations using the settings service API.
 */
export const profileService = {
  /**
   * Fetch profile by user ID
   * Uses settings service endpoint: GET /api/v1/settings/profile/:userId
   */
  async fetchProfile(userId: string): Promise<Profile | null> {
    try {
      const endpoint = `${API_ENDPOINTS.SETTINGS.PROFILE}/${userId}`;
      const profile = await apiCall<Profile>(endpoint, {
        method: "GET",
      });
      return profile;
    } catch (error: unknown) {
      // If profile not found (404), return null instead of throwing
      const err = error as Error & { status?: number; code?: string };
      if (
        err.status === 404 ||
        err.message?.includes("404") ||
        err.message?.includes("not found")
      ) {
        return null;
      }
      // Handle network change errors gracefully - these are transient network issues
      // Don't log them as errors since they're expected during network transitions
      if (
        err.code === "NETWORK_CHANGED" ||
        err.message?.includes("Network connection changed") ||
        isTransientClientFetchError(error)
      ) {
        // Silently return null for network change errors - the caller can retry if needed
        // This prevents console spam during network transitions (WiFi/VPN switches)
        return null;
      }
      // Expired/missing session while a page still tries to load profile — UX, not a bug.
      if (isExpectedClientAuthError(error)) {
        return null;
      }
      console.error("[ProfileService] fetchProfile failed:", error);
      throw error;
    }
  },

  /**
   * Update profile
   * Uses settings service endpoint: PUT /api/v1/settings/profile/:userId
   */
  async updateProfile(
    userId: string,
    updates: Partial<Profile>,
    requestOptions?: ProfileRequestOptions
  ): Promise<Profile> {
    try {
      const endpoint = `${API_ENDPOINTS.SETTINGS.PROFILE}/${userId}`;
      const profile = await apiCall<Profile>(
        endpoint,
        {
          method: "PUT",
          body: JSON.stringify(updates),
        },
        requestOptions?.signal
      );
      return profile;
    } catch (error) {
      console.error("[ProfileService] updateProfile failed:", error);
      throw error;
    }
  },

  /**
   * Upload profile avatar to DigitalOcean Spaces via settings service.
   * POST /api/settings/profile/:userId/avatar
   */
  async uploadAvatar(
    userId: string,
    file: File,
    requestOptions?: ProfileRequestOptions
  ): Promise<{ success: boolean; url: string; path: string }> {
    try {
      const endpoint = `${API_ENDPOINTS.SETTINGS.PROFILE}/${userId}/avatar`;
      const fileData = await fileToBase64(file);
      return await apiCall<{ success: boolean; url: string; path: string }>(
        endpoint,
        {
          method: "POST",
          body: JSON.stringify({
            fileName: file.name,
            fileData,
            contentType: file.type,
          }),
        },
        requestOptions?.signal
      );
    } catch (error) {
      console.error("[ProfileService] uploadAvatar failed:", error);
      throw error;
    }
  },

  /**
   * Get security settings for a profile
   * Uses auth service endpoint: GET /api/v1/auth/profile/:userId/security
   */
  async getSecuritySettings(userId: string): Promise<Record<string, unknown>> {
    try {
      const endpoint = `${API_ENDPOINTS.AUTH.BASE}/profile/${userId}/security`;
      const response = await apiCall<{ success: boolean; data: Record<string, unknown> }>(
        endpoint,
        {
          method: "GET",
        }
      );
      return response.data;
    } catch (error) {
      console.error("[ProfileService] getSecuritySettings failed:", error);
      throw error;
    }
  },

  /**
   * Update security settings for a profile
   * Uses auth service endpoint: PUT /api/v1/auth/profile/:userId/security
   */
  async updateSecuritySettings(
    userId: string,
    settings: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    try {
      const endpoint = `${API_ENDPOINTS.AUTH.BASE}/profile/${userId}/security`;
      const response = await apiCall<{ success: boolean; data: Record<string, unknown> }>(
        endpoint,
        {
          method: "PUT",
          body: JSON.stringify(settings),
        }
      );
      return response.data;
    } catch (error) {
      console.error("[ProfileService] updateSecuritySettings failed:", error);
      throw error;
    }
  },

  /**
   * Get auth metadata for a profile
   * Uses auth service endpoint: GET /api/v1/auth/profile/:userId/auth-metadata
   */
  async getAuthMetadata(userId: string): Promise<Record<string, unknown>> {
    try {
      const endpoint = `${API_ENDPOINTS.AUTH.BASE}/profile/${userId}/auth-metadata`;
      const response = await apiCall<{ success: boolean; data: Record<string, unknown> }>(
        endpoint,
        {
          method: "GET",
        }
      );
      return response.data;
    } catch (error) {
      console.error("[ProfileService] getAuthMetadata failed:", error);
      throw error;
    }
  },
};
